import { CHALLENGE_LIMITS, NICKNAME_MESSAGES, nicknameProblem } from '@cruz/shared';
import { useState, type FormEvent } from 'react';
import { load, save } from '../lib/storage.ts';
import { api, ApiError, errorMessage } from './api.ts';
import { saveGame } from './device.ts';
import { navigate } from './router.ts';
import { inputClass, Notice, primaryButton } from './ui.tsx';

const { questions: Q, seconds: S } = CHALLENGE_LIMITS;

/** /reto/nuevo: a nickname and, if wanted, two settings. One tap creates the challenge. */
export function CreateScreen() {
  const [nickname, setNickname] = useState(() => load('cruz.reto.nickname', ''));
  const [count, setCount] = useState<number>(Q.default);
  const [secs, setSecs] = useState<number>(S.default);
  const [showSettings, setShowSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = nickname.trim() ? nicknameProblem(nickname) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const p = nicknameProblem(nickname);
    if (p) return setError(NICKNAME_MESSAGES[p]);
    setBusy(true);
    setError(null);
    try {
      const r = await api.create({
        nickname,
        question_count: count,
        seconds_per_question: secs,
      });
      saveGame(r.code, { token: r.token, nickname: r.nickname });
      save('cruz.reto.nickname', r.nickname);
      navigate(r.path);
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.error === 'nickname_invalid' && err.body.reason
          ? NICKNAME_MESSAGES[err.body.reason]
          : errorMessage(err),
      );
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-6 pt-2">
      <section>
        <h1 className="font-serif text-3xl leading-snug text-purple dark:text-cream">
          Reta a tus amigos
        </h1>
        <p className="mt-2 text-lg text-ink-soft dark:text-cream/80">
          Preguntas sobre la Semana Santa de Sevilla con los datos oficiales. Comparte el enlace por
          WhatsApp: cada uno juega cuando pueda y gana quien más acierte.
        </p>
      </section>

      <div>
        <label htmlFor="nickname" className="mb-1 block text-lg font-semibold">
          Tu apodo
        </label>
        <input
          id="nickname"
          className={inputClass}
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          maxLength={40}
          autoComplete="nickname"
          enterKeyHint="go"
          placeholder="Por ejemplo, Macarena74"
          aria-invalid={problem != null}
          aria-describedby="nickname-help"
        />
        <p id="nickname-help" className="mt-1 text-sm text-ink-soft dark:text-cream/70">
          {problem ? NICKNAME_MESSAGES[problem] : 'Sin registro ni datos personales.'}
        </p>
      </div>

      <div>
        <button
          type="button"
          className="text-base text-purple underline underline-offset-4 dark:text-gold-300"
          aria-expanded={showSettings}
          onClick={() => setShowSettings((s) => !s)}
        >
          {showSettings ? 'Ocultar ajustes' : `Ajustes: ${count} preguntas, ${secs} s cada una`}
        </button>
        {showSettings && (
          <div className="mt-3 space-y-4 rounded-2xl border-2 border-purple/15 bg-white/60 p-4 dark:border-cream/15 dark:bg-night-800">
            <label className="block">
              <span className="flex justify-between text-lg">
                <span>Preguntas</span>
                <strong>{count}</strong>
              </span>
              <input
                type="range"
                min={Q.min}
                max={Q.max}
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                className="mt-2 w-full accent-purple dark:accent-gold-300"
              />
            </label>
            <label className="block">
              <span className="flex justify-between text-lg">
                <span>Segundos por pregunta</span>
                <strong>{secs}</strong>
              </span>
              <input
                type="range"
                min={S.min}
                max={S.max}
                step={5}
                value={secs}
                onChange={(e) => setSecs(Number(e.target.value))}
                className="mt-2 w-full accent-purple dark:accent-gold-300"
              />
            </label>
          </div>
        )}
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <button type="submit" className={primaryButton} disabled={busy}>
        {busy ? 'Preparando las preguntas…' : 'Crear el reto'}
      </button>
    </form>
  );
}
